import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Head, Link, router } from "@inertiajs/react";
import { AlertCircle, CheckIcon } from "lucide-react";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Logo from "../assets/fulllogo_transparent.png";
import screenshot from "../assets/screenshot-summary.png";

type Inputs = {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
  terms: boolean;
};

type PageData = {
  error: string;
  notice: string;
  form_errors?: {
    email: string[];
    password: string[];
    password_confirmation: string[];
    base: string;
  };
};

function RegistrationForm({ error, notice, form_errors }: PageData) {
  const { register, handleSubmit } = useForm<Inputs>();
  const [termsChecked, setTermsChecked] = useState(false);

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    router.post("/register", data);
  };

  return (
    <div className="mx-auto max-w-lg  ">
      {error && (
        <Alert variant="destructive" className="mb-8 max-w-sm">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </Alert>
      )}
      {notice && (
        <Alert className="mb-8 max-w-sm">
          <div className="flex items-center gap-2">
            <CheckIcon className="h-4 w-4" />
            <p className="text-sm">{notice}</p>
          </div>
        </Alert>
      )}

      <div className="mx-auto">
        <img src={Logo} className="h-12 mb-8 mx-auto" />

        <div className="w-full">
          <CardHeader>
            <CardTitle className="text-3xl">Sign up</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-4">
                {form_errors?.base && (
                  <p className="text-sm text-destructive">{form_errors.base}</p>
                )}

                <div className="space-y-1">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    className="bg-white/70"
                    required
                    {...register("first_name")}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    className="bg-white/70"
                    required
                    {...register("last_name")}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    className="bg-white/70"
                    required
                    {...register("email")}
                  />
                  {form_errors?.email && (
                    <p className="text-sm text-destructive">{form_errors.email.join(", ")}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center">
                    <Label htmlFor="password">Password</Label>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    className="bg-white/70"
                    required
                    {...register("password")}
                  />
                  {form_errors?.password && (
                    <p className="text-sm text-destructive">{form_errors.password.join(", ")}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <div className="flex items-center">
                    <Label htmlFor="password">Password Confirmation</Label>
                  </div>
                  <Input
                    id="password_confirmation"
                    type="password"
                    className="bg-white/70"
                    required
                    {...register("password_confirmation")}
                  />
                  {form_errors?.password_confirmation && (
                    <p className="text-sm text-destructive">
                      {form_errors.password_confirmation.join(", ")}
                    </p>
                  )}
                </div>

                <div className="flex gap-2 py-2">
                  <Checkbox
                    id="terms"
                    {...register("terms")}
                    checked={termsChecked}
                    onCheckedChange={() => setTermsChecked(!termsChecked)}
                  />
                  <Label htmlFor="terms">
                    I agree to the{" "}
                    <a
                      href="https://www.teeba-calculator.com/terms-of-use"
                      className="underline"
                      target="_blank"
                    >
                      Terms of Use
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://www.teeba-calculator.com/privacy-policy"
                      className="underline"
                      target="_blank"
                    >
                      Privacy Policy
                    </a>
                  </Label>
                </div>

                <div className="flex justify-center">
                  <Button type="submit" className="w-full" disabled={!termsChecked}>
                    Sign up
                  </Button>
                </div>
              </div>
            </form>

            <div className="pt-6 text-sm text-center">
              Already have an account?{" "}
              <Link href="/session/new" className="underline">
                Login
              </Link>
            </div>
          </CardContent>
        </div>
      </div>
    </div>
  );
}

export default function Component({ error, notice, form_errors }: PageData) {
  return (
    <div className="min-h-screen w-full">
      <Head title="Sign Up" />

      <div className="lg:flex lg:flex-row-reverse">
        <div className="lg:flex-none sm:px-8 lg:px-8 py-16">
          <RegistrationForm error={error} notice={notice} form_errors={form_errors} />
        </div>

        <div className="flex-1 min-h-screen bg-sky-100">
          <div className="mx-auto max-w-3xl px-4 pt-16">
            <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight">
              Start a free <br />{" "}
              <span className="underline underline-offset-[12px]">one-week</span> trial
            </h1>

            <p className="text-2xl pt-12 pb-2">
              Teeba can help with your market based adjustments, ensuring compliance with the new
              GSE requirement.
            </p>

            <a
              href="https://www.teeba-calculator.com/how-it-works"
              target="_blank"
              className="text-lg underline underline-offset-4 hover:underline-offset-8 transition-all duration-300"
            >
              Learn more about how it works
            </a>

            <div className="pt-12">
              <div className="text-2xl font-bold">Results fueled by your expertise</div>
              <div className="text-lg">
                Teeba leverages your knowledge and MLS data to calculate market trends.
              </div>
            </div>
            <div className="pt-8">
              <div className="text-2xl font-bold">Report Ready Adjustments</div>
              <div className="text-lg">
                Teeba calculates market based adjustments, ready for your appraisal reports.
              </div>
            </div>
          </div>

          <div className="mx-auto w-[90%] rounded rounded-lg my-16 p-4 bg-background shadow-2xl transform -rotate-2">
            <img src={screenshot} />
          </div>
        </div>
      </div>
    </div>
  );
}
